import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import {
  ArrowPathIcon,
  ArrowRightIcon,
  BeakerIcon,
  CheckIcon,
  ClipboardDocumentIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/20/solid";
import { Badge } from "../../../ui/Badge";
import { Button } from "../../../ui/Button";
import { Callout } from "../../../ui/Callout";
import { SidePanel } from "../../../ui/SidePanel";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../../ui/Tooltip";
import { formatDate } from "../../../ui/utils";
import { useAgentBriefCopy } from "../hooks/useAgentBriefCopy";
import { useCreateEvaluatorFromMode } from "../hooks/useCreateEvaluatorFromMode";
import type { FailureMode, FailurePoint, Granularity } from "../types";
import { modeTrend } from "../utils/trend";
import {
  newBadgeExplanation,
  trendAriaLabel,
  trendExplanation,
  windowNoun,
} from "../utils/windowCopy";

interface ModeDetailPanelProps {
  mode: FailureMode | null;
  points: FailurePoint[];
  onClose: () => void;
  /** Session-stable palette colour for a mode id (see `useModeColors`). */
  colorForMode: (id: string) => string;
  granularity: Granularity;
  /** True on the live "now" view; the "New" badge only renders live (#1273). */
  isLive: boolean;
}

// Cap the example list — the panel is for orientation, not a full trace export
// (out of scope for v1, design doc 015 §2).
const MAX_EXAMPLES = 25;

/**
 * Slide-out detail for a selected failure mode (design doc 015 §6): its size,
 * trend, status, and a sample of the in-window explanations assigned to it. The
 * examples are derived client-side from the map points already in hand.
 */
export function ModeDetailPanel({
  mode,
  points,
  onClose,
  colorForMode,
  granularity,
  isLive,
}: ModeDetailPanelProps) {
  const examples = useMemo(() => {
    if (!mode) return [];
    return points
      .filter((p) => p.failure_mode_id === mode.failure_mode_id)
      .slice(0, MAX_EXAMPLES);
  }, [mode, points]);

  const trend = mode ? modeTrend(mode.size_in_window, mode.prev_window_size) : null;
  const noun = windowNoun(granularity);

  // "Copy for coding agent": assemble a JSON brief (this mode + a few example
  // traces with full content) and copy it to the clipboard (#1324). Disabled
  // with no in-window examples — the brief's value is the reproducing traces.
  const { status: copyStatus, copy } = useAgentBriefCopy();
  const copyLabel =
    copyStatus === "copied"
      ? "Copied to clipboard"
      : copyStatus === "loading"
        ? "Preparing brief…"
        : copyStatus === "error"
          ? "Copy failed — try again"
          : "Copy for coding agent";
  const handleCopyBrief = () => {
    if (!mode) return;
    copy({ mode, granularity, isLive, examplePoints: examples });
  };

  // "Create evaluator": promote this failure mode into a "penalize responses"
  // evaluator and jump to its detail page to refine it (#1399). Needs only the
  // mode itself (its label + description), so it stays enabled even when there
  // are no in-window example traces.
  const { status: createStatus, create } = useCreateEvaluatorFromMode();
  const createLabel =
    createStatus === "loading"
      ? "Creating evaluator…"
      : createStatus === "error"
        ? "Couldn't create — try again"
        : "Create evaluator";
  const handleCreateEvaluator = () => {
    if (!mode) return;
    create(mode);
  };

  return (
    <SidePanel
      isOpen={mode !== null}
      onClose={onClose}
      title={mode?.label ?? ""}
      width="640px"
    >
      {mode && (
        <TooltipProvider>
          <div className="space-y-5">
            <div className="flex items-center gap-2">
              <span
                className="h-3 w-3 flex-shrink-0 rounded-full"
                style={{ backgroundColor: colorForMode(mode.failure_mode_id) }}
                aria-hidden="true"
              />
              <Badge variant="outline">{mode.size_in_window} in window</Badge>
              {trend && trend.direction !== "flat" && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span>
                      <Badge
                        variant={trend.direction === "up" ? "error" : "success"}
                        aria-label={trendAriaLabel(
                          granularity,
                          trend.direction,
                          trend.delta,
                          trend.pct,
                        )}
                      >
                        {trend.direction === "up" ? "▲" : "▼"}{" "}
                        {trend.delta > 0 ? "+" : ""}
                        {trend.delta} vs prev {noun}
                      </Badge>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>{trendExplanation(granularity)}</TooltipContent>
                </Tooltip>
              )}
              {isLive && mode.is_new && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span>
                      <Badge variant="success">New</Badge>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    {newBadgeExplanation(granularity)}
                  </TooltipContent>
                </Tooltip>
              )}
              {mode.status === "dormant" && <Badge>Dormant</Badge>}
            </div>

            <p className="text-sm text-muted-foreground">
              First seen {formatDate(mode.first_seen_at)} ·{" "}
              {mode.prev_window_size} in the previous {noun}
            </p>

            <div className="space-y-3 rounded-md border border-border bg-background p-3">
              {/* Promote this failure mode into a "penalize responses" evaluator
                  and open it (#1399) — the primary next step for turning a
                  discovered mode into a tracked criterion. Needs only the mode
                  itself, so unlike the brief it is never disabled for want of
                  in-window examples. */}
              <div>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span>
                      <Button
                        type="button"
                        variant="primary"
                        size="sm"
                        onClick={handleCreateEvaluator}
                        disabled={createStatus === "loading"}
                        aria-live="polite"
                      >
                        {createStatus === "loading" ? (
                          <ArrowPathIcon
                            className="h-4 w-4 animate-spin"
                            aria-hidden="true"
                          />
                        ) : createStatus === "error" ? (
                          <ExclamationTriangleIcon
                            className="h-4 w-4"
                            aria-hidden="true"
                          />
                        ) : (
                          <BeakerIcon className="h-4 w-4" aria-hidden="true" />
                        )}
                        {createLabel}
                      </Button>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    Turns this failure mode into a “penalize responses”
                    evaluator and opens it, so you can track and grade against
                    it like any other criterion. You can refine the wording
                    before it runs.
                  </TooltipContent>
                </Tooltip>
              </div>

              {/* Copy for coding agent (#1324). */}
              <div>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span>
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        onClick={handleCopyBrief}
                        disabled={
                          examples.length === 0 || copyStatus === "loading"
                        }
                        aria-live="polite"
                      >
                        {copyStatus === "copied" ? (
                          <CheckIcon className="h-4 w-4" aria-hidden="true" />
                        ) : copyStatus === "loading" ? (
                          <ArrowPathIcon
                            className="h-4 w-4 animate-spin"
                            aria-hidden="true"
                          />
                        ) : copyStatus === "error" ? (
                          <ExclamationTriangleIcon
                            className="h-4 w-4"
                            aria-hidden="true"
                          />
                        ) : (
                          <ClipboardDocumentIcon
                            className="h-4 w-4"
                            aria-hidden="true"
                          />
                        )}
                        {copyLabel}
                      </Button>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    Copies a JSON brief — this failure mode plus example traces
                    with full content — ready to paste into Claude Code or
                    another coding agent to investigate and fix.
                    {examples.length === 0 && (
                      <span className="mt-1 block text-muted-foreground">
                        No example traces in this window to include.
                      </span>
                    )}
                  </TooltipContent>
                </Tooltip>
              </div>
            </div>

            {mode.description && (
              <div>
                <h3 className="mb-2 text-sm font-semibold text-foreground">
                  What this failure mode is
                </h3>
                <p className="whitespace-pre-wrap text-sm text-muted-foreground">
                  {mode.description}
                </p>
              </div>
            )}

            <div>
              <h3 className="mb-2 text-sm font-semibold text-foreground">
                Example failures
              </h3>
              {examples.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No example explanations available in this window.
                </p>
              ) : (
                <div className="space-y-2">
                  {examples.map((point) => (
                    // Deep-link to the trace's detail on the Traces page
                    // (#1278), in a new tab so the clusters map stays put.
                    <Link
                      key={point.trace_id}
                      to={`/traces?trace=${encodeURIComponent(point.trace_id)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="link-reset block"
                    >
                      <Callout className="transition-colors hover:border-primary/40 hover:bg-accent">
                        <p className="whitespace-pre-wrap">
                          {point.explanation}
                        </p>
                        <div className="mt-1 flex items-center justify-between gap-2">
                          <span className="text-xs text-muted-foreground">
                            {formatDate(point.created_at)}
                          </span>
                          <span className="flex items-center gap-0.5 text-xs font-medium text-primary">
                            View trace
                            <ArrowRightIcon
                              className="h-3 w-3"
                              aria-hidden="true"
                            />
                          </span>
                        </div>
                      </Callout>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>
        </TooltipProvider>
      )}
    </SidePanel>
  );
}
